/**
 * Lenis Smooth Scroll Integration
 *
 * Apple-style smooth scrolling with GSAP ScrollTrigger integration.
 * Respects prefers-reduced-motion accessibility setting.
 */

import Lenis from 'lenis';
import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';

// Register GSAP plugins
gsap.registerPlugin(ScrollTrigger);

// Store Lenis instance globally
let lenisInstance = null;

/**
 * Check if user prefers reduced motion
 */
function prefersReducedMotion() {
  return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
}

/**
 * Check if device is a touch/mobile device
 * Lenis causes scrolling issues on iOS Safari and other mobile browsers
 */
function isTouchDevice() {
  return 'ontouchstart' in window || navigator.maxTouchPoints > 0;
}

/**
 * Initialize Lenis smooth scroll
 * @returns {Lenis|null} Lenis instance or null if reduced motion is preferred
 */
export function initLenis() {
  // Don't initialize if reduced motion is preferred
  if (prefersReducedMotion()) {
    console.log('[Lenis] Skipping initialization - prefers-reduced-motion enabled');
    return null;
  }

  // Don't initialize on touch/mobile devices - causes scroll issues on iOS
  if (isTouchDevice()) {
    console.log('[Lenis] Skipping initialization - touch device detected');
    return null;
  }

  // Don't double-initialize
  if (lenisInstance) {
    console.log('[Lenis] Already initialized');
    return lenisInstance;
  }

  try {
    // Create Lenis instance with Apple-style configuration
    lenisInstance = new Lenis({
      duration: 1.2,                    // Smooth scroll duration
      easing: (t) => Math.min(1, 1.001 - Math.pow(2, -10 * t)), // Exponential ease out
      orientation: 'vertical',          // Vertical scroll only
      gestureOrientation: 'vertical',   // Gesture direction
      smoothWheel: true,                // Smooth mouse wheel
      wheelMultiplier: 1,               // Wheel speed (1 = normal)
      touchMultiplier: 2,               // Touch scroll speed
      infinite: false,                  // No infinite scroll
      autoResize: true,                 // Auto handle resize
    });

    // Integrate with GSAP ScrollTrigger
    lenisInstance.on('scroll', ScrollTrigger.update);

    // Use GSAP ticker for smooth animation frame sync
    gsap.ticker.add((time) => {
      lenisInstance.raf(time * 1000);
    });

    // Disable GSAP's lag smoothing for better Lenis sync
    gsap.ticker.lagSmoothing(0);

    // Handle anchor link smooth scrolling
    setupAnchorLinks(lenisInstance);

    // Expose globally for debugging
    window.lenis = lenisInstance;

    console.log('[Lenis] Initialized successfully');
    return lenisInstance;

  } catch (error) {
    console.error('[Lenis] Failed to initialize:', error);
    return null;
  }
}

/**
 * Set up smooth scrolling for anchor links
 * @param {Lenis} lenis - Lenis instance
 */
function setupAnchorLinks(lenis) {
  document.querySelectorAll('a[href^="#"]').forEach(anchor => {
    anchor.addEventListener('click', (e) => {
      const href = anchor.getAttribute('href');

      // Skip empty anchors or just "#"
      if (!href || href === '#') return;

      const target = document.querySelector(href);
      if (target) {
        e.preventDefault();

        // Get header height for offset
        const header = document.querySelector('header');
        const headerHeight = header ? header.offsetHeight : 0;

        // Scroll to target with offset
        lenis.scrollTo(target, {
          offset: -(headerHeight + 20), // Account for fixed header + padding
          duration: 1.2,
        });
      }
    });
  });
}

/**
 * Destroy Lenis instance and clean up
 */
export function destroyLenis() {
  if (lenisInstance) {
    lenisInstance.destroy();
    lenisInstance = null;
    window.lenis = null;
    console.log('[Lenis] Destroyed');
  }
}

/**
 * Get the current Lenis instance
 * @returns {Lenis|null}
 */
export function getLenis() {
  return lenisInstance;
}

/**
 * Pause Lenis scrolling (useful for modals/menus)
 */
export function pauseLenis() {
  if (lenisInstance) {
    lenisInstance.stop();
  }
}

/**
 * Resume Lenis scrolling
 */
export function resumeLenis() {
  if (lenisInstance) {
    lenisInstance.start();
  }
}

/**
 * Scroll to a specific element or position
 * @param {string|HTMLElement|number} target - Target element, selector, or position
 * @param {Object} options - Scroll options
 */
export function scrollTo(target, options = {}) {
  if (lenisInstance) {
    lenisInstance.scrollTo(target, {
      offset: 0,
      duration: 1.2,
      ...options,
    });
  } else {
    // Fallback for when Lenis is not available
    let targetElement;
    if (typeof target === 'string') {
      targetElement = document.querySelector(target);
    } else if (target instanceof HTMLElement) {
      targetElement = target;
    }

    if (targetElement) {
      targetElement.scrollIntoView({ behavior: 'smooth' });
    } else if (typeof target === 'number') {
      window.scrollTo({ top: target, behavior: 'smooth' });
    }
  }
}

export default {
  init: initLenis,
  destroy: destroyLenis,
  get: getLenis,
  pause: pauseLenis,
  resume: resumeLenis,
  scrollTo,
};
